/**
 * Remembers how a visitor arrived, for the length of the browser session,
 * so the product-match quiz can report it to Klaviyo.
 *
 * Ads send people to a landing page with `utm_*` parameters. Neither the
 * parameters nor the landing path survive the click through to
 * `/pages/quiz`, so this module captures them on every page load and the
 * quiz reads them back when it writes the `Quiz Completed` event. First
 * touch wins within the session: the landing page is the first page seen,
 * the UTM parameters are the first set seen.
 *
 * The advertising angle is not tracked here. The quiz resolves it itself
 * from `?angle=`, the referring landing page, or the session memory the
 * landing pages write (see snippets/quiz-angle-memory.liquid).
 *
 * Nothing personal is stored: only the parameters, the landing path, and a
 * timestamp, under the `tiiga-attribution` sessionStorage key.
 */

export const ATTRIBUTION_STORAGE_KEY = 'tiiga-attribution';

/** The campaign parameters the quiz reports, in the order they are reported. */
export const UTM_PARAMS = /** @type {const} */ ([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
]);

/**
 * @typedef {(typeof UTM_PARAMS)[number]} UtmParam
 *
 * @typedef {object} Attribution
 * @property {string} landing_page - The first path seen this session.
 * @property {string} at - ISO timestamp of that first page view.
 * @property {string} [utm_source]
 * @property {string} [utm_medium]
 * @property {string} [utm_campaign]
 * @property {string} [utm_content]
 * @property {string} [utm_term]
 */

/**
 * Reads the attribution remembered for this session.
 *
 * @returns {Attribution | null}
 */
export function readAttribution() {
  try {
    const stored = sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    return stored ? /** @type {Attribution} */ (JSON.parse(stored)) : null;
  } catch {
    return null;
  }
}

/**
 * Records the landing page on the first page view of the session and the
 * UTM parameters on the first page that carries any; later pages never
 * overwrite either.
 */
function captureAttribution() {
  const existing = readAttribution();
  if (existing && UTM_PARAMS.some((key) => key in existing)) return;

  const params = new URLSearchParams(location.search);
  /** @type {Partial<Record<UtmParam, string>>} */
  const utm = {};
  for (const key of UTM_PARAMS) {
    const value = params.get(key)?.trim();
    if (value) utm[key] = value.slice(0, 120);
  }
  if (existing && Object.keys(utm).length === 0) return;

  /** @type {Attribution} */
  const next = {
    landing_page: existing?.landing_page ?? location.pathname,
    at: existing?.at ?? new Date().toISOString(),
    ...utm,
  };

  try {
    sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage can be blocked (private mode, disabled cookies); attribution is best-effort.
  }
}

captureAttribution();
